import { useEffect, useRef, useCallback, useState } from 'react'
import grapesjs, { Editor } from 'grapesjs'
import 'grapesjs/dist/css/grapes.min.css'
import 'grapesjs-preset-webpage'
import { Button } from '@/components/ui/Button'
import {
  Save, Eye, Undo, Redo, Code, Trash2,
  Layers, Palette, LayoutGrid, Smartphone, Monitor, Tablet,
  ChevronLeft, ChevronRight, Image as ImageIcon, Settings2,
  Maximize2, Minimize2, Upload, Loader2
} from 'lucide-react'
import { useImageUpload } from '../../hooks/useImageUpload'

// Stock images for the asset manager
const STOCK_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&h=600&fit=crop', name: 'Medical team' },
  { src: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=800&h=600&fit=crop', name: 'Healthcare' },
  { src: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=600&fit=crop', name: 'Doctor consultation' },
  { src: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800&h=600&fit=crop', name: 'Medical equipment' },
  { src: 'https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=800&h=600&fit=crop', name: 'Hospital' },
  { src: 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=800&h=600&fit=crop', name: 'Stethoscope' },
  { src: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=600&fit=crop', name: 'Pharmacy' },
  { src: 'https://images.unsplash.com/photo-1551076805-e1869033e561?w=800&h=600&fit=crop', name: 'Lab research' },
]

interface GrapesEditorProps {
  initialHtml?: string
  initialCss?: string
  initialGjsData?: Record<string, unknown>
  onSave: (data: { html: string; css: string; gjsData: Record<string, unknown> }) => void
  onPreview?: () => void
  saving?: boolean
}

type PanelTab = 'blocks' | 'layers' | 'styles' | 'traits' | 'assets'

export function GrapesEditor({
  initialHtml = '',
  initialCss = '',
  initialGjsData,
  onSave,
  onPreview,
  saving = false,
}: GrapesEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const editorInstance = useRef<Editor | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isReady, setIsReady] = useState(false)
  const [activeTab, setActiveTab] = useState<PanelTab>('blocks')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [customImageUrl, setCustomImageUrl] = useState('')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [hasLinkSelected, setHasLinkSelected] = useState(false)
  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const { upload, isUploading, progress, error: uploadError } = useImageUpload()

  const handleSave = useCallback(() => {
    if (!editorInstance.current) return

    const editor = editorInstance.current
    const html = editor.getHtml()
    const css = editor.getCss()
    const gjsData = editor.getProjectData()

    onSave({ html: html || '', css: css || '', gjsData })
  }, [onSave])

  const handleUndo = useCallback(() => {
    editorInstance.current?.UndoManager.undo()
  }, [])

  const handleRedo = useCallback(() => {
    editorInstance.current?.UndoManager.redo()
  }, [])

  const handleClear = useCallback(() => {
    if (window.confirm('Voulez-vous vraiment effacer tout le contenu ?')) {
      editorInstance.current?.DomComponents.clear()
    }
  }, [])

  const handleToggleCode = useCallback(() => {
    editorInstance.current?.runCommand('open-code')
  }, [])

  const handleDeviceChange = useCallback((device: string) => {
    editorInstance.current?.setDevice(device)
  }, [])

  const handleInsertImage = useCallback((imageUrl: string) => {
    if (!editorInstance.current || !imageUrl) return

    const editor = editorInstance.current
    const selected = editor.getSelected()

    if (selected) {
      // If an image is selected, change its src
      if (selected.get('tagName') === 'img') {
        selected.set('src', imageUrl)
        selected.addAttributes({ src: imageUrl })
      } else {
        // If element has background-image style, update it
        const styles = selected.getStyle() as Record<string, string>
        const currentBg = styles['background-image']
        if (currentBg && currentBg !== 'none') {
          selected.setStyle({ ...styles, 'background-image': `url('${imageUrl}')` })
        } else {
          // Insert new image inside selected container
          selected.append(`<img src="${imageUrl}" alt="Image" style="width: 100%; height: auto; border-radius: 8px;" />`)
        }
      }
    } else {
      // No selection, add to canvas
      editor.addComponents(`<img src="${imageUrl}" alt="Image" style="width: 100%; max-width: 600px; height: auto; border-radius: 8px; display: block; margin: 0 auto;" />`)
    }

    setCustomImageUrl('')
  }, [])

  const handleAddCustomUrl = useCallback(() => {
    if (customImageUrl.trim()) {
      handleInsertImage(customImageUrl.trim())
    }
  }, [customImageUrl, handleInsertImage])

  const handleFileUpload = useCallback(async (file: File) => {
    const result = await upload(file)
    if (result.success && result.url) {
      handleInsertImage(result.url)
    }
  }, [upload, handleInsertImage])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileUpload(file)
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [handleFileUpload])

  const handleDropFile = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDraggingFile(false)
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file)
    }
  }, [handleFileUpload])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDraggingFile(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDraggingFile(false)
  }, [])

  useEffect(() => {
    if (!editorRef.current || editorInstance.current) return

    const editor = grapesjs.init({
      container: editorRef.current,
      height: '100%',
      width: 'auto',
      storageManager: false,
      plugins: ['grapesjs-preset-webpage'],
      pluginsOpts: {
        'grapesjs-preset-webpage': {
          blocksBasicOpts: {
            blocks: ['column1', 'column2', 'column3', 'column3-7', 'text', 'link', 'image', 'video'],
            flexGrid: true,
          },
          navbarOpts: false,
          countdownOpts: false,
          formsOpts: false,
        },
      },
      // Disable base64 image embedding to prevent memory issues
      // Images must be added via URL (from the Images panel or external URLs)
      assetManager: {
        upload: false,           // Disable file upload
        uploadFile: undefined,   // No upload handler
        embedAsBase64: false,    // Reject base64 embedding
        dropzone: false,         // Disable drag-drop zone
        openAssetsOnDrop: false, // Don't open assets panel on drop
        assets: STOCK_IMAGES.map(img => ({ src: img.src, name: img.name })),
      },
      // Devices for responsive preview
      deviceManager: {
        devices: [
          { name: 'Desktop', width: '' },
          { name: 'Tablet', width: '768px', widthMedia: '992px' },
          { name: 'Mobile', width: '320px', widthMedia: '480px' },
        ],
      },
      // Panels configuration - we'll render our own panels
      panels: { defaults: [] },
      // Block manager - categories open by default
      blockManager: {
        appendTo: '#blocks-container',
      },
      // Layer manager
      layerManager: {
        appendTo: '#layers-container',
      },
      // Style manager
      styleManager: {
        appendTo: '#styles-container',
        sectors: [
          {
            name: 'Dimension',
            open: true,
            buildProps: ['width', 'height', 'max-width', 'min-height', 'margin', 'padding'],
          },
          {
            name: 'Typographie',
            open: false,
            buildProps: ['font-family', 'font-size', 'font-weight', 'letter-spacing', 'color', 'line-height', 'text-align', 'text-decoration'],
          },
          {
            name: 'Fond & Bordures',
            open: false,
            buildProps: ['background-color', 'background-image', 'background-size', 'background-position', 'background-repeat', 'border-radius', 'border', 'box-shadow'],
          },
          {
            name: 'Flexbox',
            open: false,
            buildProps: ['display', 'flex-direction', 'justify-content', 'align-items', 'gap', 'flex-wrap'],
          },
          {
            name: 'Position',
            open: false,
            buildProps: ['position', 'top', 'right', 'bottom', 'left', 'z-index'],
          },
        ],
      },
      // Selector manager
      selectorManager: {
        appendTo: '#styles-container',
      },
      // Trait manager (for editing attributes like href, src, alt, etc.)
      traitManager: {
        appendTo: '#traits-container',
      },
      // Canvas styles
      canvas: {
        styles: [
          'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap',
        ],
      },
    })

    // =====================
    // BLOCS TESSAN
    // =====================

    // Catégorie: Basique
    editor.BlockManager.add('tessan-heading', {
      label: `<div class="gjs-block-label">
        <div style="font-size: 24px; font-weight: bold; margin-bottom: 4px;">H</div>
        <span>Titre</span>
      </div>`,
      category: 'Basique',
      content: `<h2 style="font-family: 'Recoleta', serif; color: #0F352D; font-size: 2rem; margin-bottom: 1rem;">Titre de section</h2>`,
    })

    editor.BlockManager.add('tessan-text', {
      label: `<div class="gjs-block-label">
        <div style="font-size: 18px; margin-bottom: 4px;">Aa</div>
        <span>Texte</span>
      </div>`,
      category: 'Basique',
      content: `<p style="font-family: 'Plus Jakarta Sans', sans-serif; color: #333; font-size: 1rem; line-height: 1.6;">Votre texte ici. Double-cliquez pour éditer.</p>`,
    })

    editor.BlockManager.add('tessan-button-primary', {
      label: `<div class="gjs-block-label">
        <div style="background: #0F352D; color: white; padding: 4px 12px; border-radius: 4px; font-size: 12px; margin-bottom: 4px;">Bouton</div>
        <span>Bouton Vert</span>
      </div>`,
      category: 'Basique',
      content: `<a href="#" style="display: inline-block; background-color: #0F352D; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;">Bouton</a>`,
    })

    editor.BlockManager.add('tessan-button-secondary', {
      label: `<div class="gjs-block-label">
        <div style="background: #FFF198; color: #0F352D; padding: 4px 12px; border-radius: 4px; font-size: 12px; margin-bottom: 4px;">Bouton</div>
        <span>Bouton Jaune</span>
      </div>`,
      category: 'Basique',
      content: `<a href="#" style="display: inline-block; background-color: #FFF198; color: #0F352D; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;">Bouton</a>`,
    })

    // Catégorie: Sections
    editor.BlockManager.add('tessan-banner', {
      label: `<div class="gjs-block-label">
        <div style="background: linear-gradient(135deg, #0F352D, #1d5c4d); color: white; padding: 8px; border-radius: 4px; font-size: 11px; margin-bottom: 4px; text-align: center;">BANNIÈRE</div>
        <span>Bannière</span>
      </div>`,
      category: 'Sections',
      content: `<div style="background: linear-gradient(135deg, #0F352D 0%, #1d5c4d 100%); padding: 48px 24px; text-align: center;">
        <h1 style="font-family: 'Recoleta', serif; color: white; font-size: 2.5rem; margin-bottom: 1rem;">Titre de la bannière</h1>
        <p style="font-family: 'Plus Jakarta Sans', sans-serif; color: #fcf5cc; font-size: 1.125rem; margin-bottom: 1.5rem;">Sous-titre descriptif de la bannière</p>
        <a href="#" style="display: inline-block; background-color: #FFF198; color: #0F352D; padding: 12px 32px; border-radius: 8px; text-decoration: none; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;">Action</a>
      </div>`,
    })

    editor.BlockManager.add('tessan-card', {
      label: `<div class="gjs-block-label">
        <div style="background: white; border: 1px solid #e5e7eb; padding: 8px; border-radius: 6px; font-size: 11px; margin-bottom: 4px;">📄 Carte</div>
        <span>Carte</span>
      </div>`,
      category: 'Sections',
      content: `<div style="background-color: white; border-radius: 12px; padding: 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
        <h3 style="font-family: 'Recoleta', serif; color: #0F352D; font-size: 1.25rem; margin-bottom: 0.5rem;">Titre de la carte</h3>
        <p style="font-family: 'Plus Jakarta Sans', sans-serif; color: #666; font-size: 0.875rem;">Description de la carte. Double-cliquez pour éditer ce texte.</p>
      </div>`,
    })

    editor.BlockManager.add('tessan-cta-section', {
      label: `<div class="gjs-block-label">
        <div style="background: #fcf5cc; color: #0F352D; padding: 8px; border-radius: 4px; font-size: 11px; margin-bottom: 4px; text-align: center;">CTA</div>
        <span>Section CTA</span>
      </div>`,
      category: 'Sections',
      content: `<div style="background-color: #fcf5cc; padding: 32px 24px; text-align: center; border-radius: 12px;">
        <h2 style="font-family: 'Recoleta', serif; color: #0F352D; font-size: 1.75rem; margin-bottom: 0.5rem;">Prêt à commencer ?</h2>
        <p style="font-family: 'Plus Jakarta Sans', sans-serif; color: #0F352D; font-size: 1rem; margin-bottom: 1rem;">Rejoignez notre communauté de professionnels de santé.</p>
        <a href="#" style="display: inline-block; background-color: #0F352D; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 600;">S'inscrire</a>
      </div>`,
    })

    // Catégorie: Événements
    editor.BlockManager.add('tessan-events-section', {
      label: `<div class="gjs-block-label">
        <div style="font-size: 20px; margin-bottom: 4px;">📅</div>
        <span>Événements</span>
      </div>`,
      category: 'Événements',
      content: `<div>
        <h3 style="font-family: 'Recoleta', serif; font-size: 18px; font-weight: 600; color: #0F352D; margin: 0 0 16px 0;">
          Événements à venir
        </h3>
        <div style="display: flex; flex-direction: column; gap: 12px;">
          <div style="background: #f9fafb; border-radius: 8px; padding: 12px; border-left: 3px solid #0F352D;">
            <div style="font-weight: 600; color: #0F352D; font-size: 14px; margin-bottom: 4px;">Titre de l'événement</div>
            <div style="color: #6b7280; font-size: 13px;">lun. 1 jan. à 14h00</div>
          </div>
        </div>
      </div>`,
    })

    editor.BlockManager.add('tessan-event-card', {
      label: `<div class="gjs-block-label">
        <div style="background: #f9fafb; border-left: 3px solid #0F352D; padding: 4px 8px; font-size: 10px; margin-bottom: 4px;">Event</div>
        <span>Carte Event</span>
      </div>`,
      category: 'Événements',
      content: `<div style="background: #f9fafb; border-radius: 8px; padding: 12px; border-left: 3px solid #0F352D;">
        <div style="font-weight: 600; color: #0F352D; font-size: 14px; margin-bottom: 4px;">Titre de l'événement</div>
        <div style="color: #6b7280; font-size: 13px;">lun. 1 jan. à 14h00</div>
      </div>`,
    })

    editor.BlockManager.add('tessan-event-featured', {
      label: `<div class="gjs-block-label">
        <div style="background: linear-gradient(135deg, #0F352D, #1d5c4d); color: white; padding: 4px 8px; border-radius: 4px; font-size: 10px; margin-bottom: 4px;">⭐ VIP</div>
        <span>Event Important</span>
      </div>`,
      category: 'Événements',
      content: `<div style="background: linear-gradient(135deg, #0F352D 0%, #1d5c4d 100%); border-radius: 8px; padding: 16px; color: white;">
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
          <span style="background: #FFF198; color: #0F352D; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600;">À NE PAS MANQUER</span>
        </div>
        <div style="font-weight: 600; font-size: 16px; margin-bottom: 4px;">Webinaire exceptionnel</div>
        <div style="opacity: 0.9; font-size: 13px; margin-bottom: 12px;">mer. 15 jan. à 18h00</div>
        <a href="#" style="display: inline-block; background: #FFF198; color: #0F352D; padding: 8px 16px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 13px;">S'inscrire</a>
      </div>`,
    })

    // Catégorie: Footer
    editor.BlockManager.add('tessan-footer', {
      label: `<div class="gjs-block-label">
        <div style="background: #fcf5cc; padding: 6px; border-radius: 4px; font-size: 10px; margin-bottom: 4px; text-align: center;">FOOTER</div>
        <span>Footer Complet</span>
      </div>`,
      category: 'Footer',
      content: `<footer style="background: #fcf5cc; border-top: 1px solid rgba(15, 53, 45, 0.1); padding: 32px 16px;">
        <div style="max-width: 1200px; margin: 0 auto; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 24px;">
          <div style="text-align: left;">
            <div style="margin-bottom: 8px;"><svg width="100" height="22" viewBox="0 0 91 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Tessan"><path d="M 91 19.8046 V 0.195405 H 88.9459 V 11.484 L 86.8351 1.28598 C 86.7219 0.726168 86.5224 0.192765 85.3794 0.192765 H 84.4386 V 19.802 H 86.4927 V 8.84869 L 88.6898 18.5424 C 88.776 18.9358 88.9458 19.802 90.0592 19.802 H 91.0027 L 91 19.8046 Z M 75.3243 19.8046 H 73.0707 L 71.2726 6.3005 L 69.4745 19.8046 H 67.2209 L 70.2482 0.195405 H 71.3616 C 72.2188 0.195405 72.359 0.728809 72.4452 1.25957 L 75.327 19.8046 H 75.3243 Z M 57.2494 4.45207 C 57.5055 4.31212 57.8209 4.00317 57.8209 3.35886 V 2.60364 C 57.8209 1.25957 56.7075 0.0264061 55.3381 0 H 54.0549 C 52.6289 0 51.4859 1.23317 51.4859 2.60364 V 4.62107 C 51.4859 4.62107 51.3996 5.93874 52.3997 7.73171 L 54.8529 12.2419 C 54.8529 12.2419 55.7371 13.755 55.7371 14.9036 V 17.1455 C 55.7371 17.7608 55.2518 18.2387 54.6534 18.2387 C 54.0549 18.2387 53.5697 17.7634 53.5697 17.1745 V 13.7576 C 53.0575 13.982 52.2272 14.3464 52.0574 14.4019 C 51.8013 14.5419 51.4859 14.8508 51.4859 15.4951 V 17.5125 C 51.5155 18.8566 52.5992 19.9498 53.9687 20.0053 H 55.2518 C 56.6779 20.0053 57.8209 18.8857 57.8209 17.5125 V 15.0198 C 57.8209 15.0198 57.9071 13.7021 56.907 11.9092 L 54.4539 7.36995 C 54.4539 7.36995 53.5697 5.88593 53.5697 4.73726 V 2.85978 C 53.5697 2.24452 54.0549 1.76657 54.6534 1.76657 C 55.2518 1.76657 55.7371 2.24188 55.7371 2.85978 V 5.12807 C 56.2789 4.90362 57.0768 4.53921 57.2494 4.45471 V 4.45207 Z M 40.1449 4.45207 C 40.401 4.31212 40.7164 4.00317 40.7164 3.35886 V 2.60364 C 40.7164 1.25957 39.6031 0.0264061 38.2336 0 H 36.9505 C 35.5244 0 34.3814 1.23317 34.3814 2.60364 V 4.62107 C 34.3814 4.62107 34.2952 5.93874 35.2953 7.73171 L 37.7484 12.2419 C 37.7484 12.2419 38.6326 13.755 38.6326 14.9036 V 17.1455 C 38.6326 17.7608 38.1474 18.2387 37.5489 18.2387 C 36.9505 18.2387 36.4652 17.7634 36.4652 17.1745 V 13.7576 C 35.953 13.982 35.1255 14.3464 34.9529 14.4019 C 34.6968 14.5419 34.3814 14.8508 34.3814 15.4951 V 17.5125 C 34.4111 18.8566 35.4948 19.9498 36.8642 20.0053 H 38.1474 C 39.5734 20.0053 40.7164 18.8857 40.7164 17.5125 V 15.0198 C 40.7164 15.0198 40.8027 13.7021 39.8026 11.9092 L 37.3494 7.36995 C 37.3494 7.36995 36.4652 5.88593 36.4652 4.73726 V 2.85978 C 36.4652 2.24452 36.9505 1.76657 37.5489 1.76657 C 38.1474 1.76657 38.6326 2.24188 38.6326 2.85978 V 5.12807 C 39.1745 4.90362 39.9724 4.53921 40.1449 4.45471 V 4.45207 Z M 20.1884 17.8162 V 11.0087 C 20.1884 10.7552 20.4175 10.5334 20.6736 10.5334 H 21.5309 C 22.1859 10.5334 22.4717 10.2535 22.6145 10 C 22.7008 9.831 23.0432 9.02033 23.2723 8.51598 H 20.1911 V 2.68814 C 20.1911 2.40824 20.3906 2.21283 20.6763 2.21283 H 22.3315 C 22.9865 2.21283 23.302 1.90388 23.4152 1.65302 L 24.0999 0.195405 H 18.765 C 18.4227 0.195405 18.1666 0.448904 18.1369 0.755215 V 19.8046 H 22.7008 C 23.3559 19.8046 23.6713 19.5247 23.8141 19.2712 C 23.9004 19.1022 24.2428 18.3179 24.4692 17.8136 H 20.1911 L 20.1884 17.8162 Z M 8.78541 0.195405 H 0.628107 C 0.285748 0.195405 0 0.47531 0 0.810668 V 2.18379 H 2.88174 V 19.3847 C 2.88174 19.5247 2.96801 19.8046 3.45324 19.8046 H 4.90894 V 2.66174 C 4.93859 2.40824 5.13808 2.18643 5.42113 2.18643 H 7.0197 C 7.67476 2.18643 7.99016 1.90652 8.10339 1.65302 L 8.7881 0.195405 H 8.78541 Z" fill="#0F352D"/></svg></div>
            <p style="font-size: 14px; color: #6b7280; margin: 0;">© 2025 Tessan. Tous droits réservés.</p>
          </div>
          <nav style="display: flex; flex-wrap: wrap; gap: 24px; font-size: 14px;">
            <a href="/faq" style="color: #4b5563; text-decoration: none;">FAQ</a>
            <a href="/content" style="color: #4b5563; text-decoration: none;">Ressources</a>
            <a href="https://www.tessan.io" target="_blank" style="color: #4b5563; text-decoration: none;">tessan.io</a>
          </nav>
          <a href="/community" style="background: #0F352D; color: white; padding: 10px 20px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 14px;">Rejoindre</a>
        </div>
      </footer>`,
    })

    editor.BlockManager.add('tessan-footer-simple', {
      label: `<div class="gjs-block-label">
        <div style="background: #0F352D; color: #FFF198; padding: 6px; border-radius: 4px; font-size: 10px; margin-bottom: 4px; text-align: center;">FOOTER</div>
        <span>Footer Simple</span>
      </div>`,
      category: 'Footer',
      content: `<footer style="background: #0F352D; padding: 24px 16px; text-align: center;">
        <div style="margin-bottom: 8px; display: flex; justify-content: center;"><svg width="90" height="20" viewBox="0 0 91 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Tessan"><path d="M 91 19.8046 V 0.195405 H 88.9459 V 11.484 L 86.8351 1.28598 C 86.7219 0.726168 86.5224 0.192765 85.3794 0.192765 H 84.4386 V 19.802 H 86.4927 V 8.84869 L 88.6898 18.5424 C 88.776 18.9358 88.9458 19.802 90.0592 19.802 H 91.0027 L 91 19.8046 Z M 75.3243 19.8046 H 73.0707 L 71.2726 6.3005 L 69.4745 19.8046 H 67.2209 L 70.2482 0.195405 H 71.3616 C 72.2188 0.195405 72.359 0.728809 72.4452 1.25957 L 75.327 19.8046 H 75.3243 Z M 57.2494 4.45207 C 57.5055 4.31212 57.8209 4.00317 57.8209 3.35886 V 2.60364 C 57.8209 1.25957 56.7075 0.0264061 55.3381 0 H 54.0549 C 52.6289 0 51.4859 1.23317 51.4859 2.60364 V 4.62107 C 51.4859 4.62107 51.3996 5.93874 52.3997 7.73171 L 54.8529 12.2419 C 54.8529 12.2419 55.7371 13.755 55.7371 14.9036 V 17.1455 C 55.7371 17.7608 55.2518 18.2387 54.6534 18.2387 C 54.0549 18.2387 53.5697 17.7634 53.5697 17.1745 V 13.7576 C 53.0575 13.982 52.2272 14.3464 52.0574 14.4019 C 51.8013 14.5419 51.4859 14.8508 51.4859 15.4951 V 17.5125 C 51.5155 18.8566 52.5992 19.9498 53.9687 20.0053 H 55.2518 C 56.6779 20.0053 57.8209 18.8857 57.8209 17.5125 V 15.0198 C 57.8209 15.0198 57.9071 13.7021 56.907 11.9092 L 54.4539 7.36995 C 54.4539 7.36995 53.5697 5.88593 53.5697 4.73726 V 2.85978 C 53.5697 2.24452 54.0549 1.76657 54.6534 1.76657 C 55.2518 1.76657 55.7371 2.24188 55.7371 2.85978 V 5.12807 C 56.2789 4.90362 57.0768 4.53921 57.2494 4.45471 V 4.45207 Z M 40.1449 4.45207 C 40.401 4.31212 40.7164 4.00317 40.7164 3.35886 V 2.60364 C 40.7164 1.25957 39.6031 0.0264061 38.2336 0 H 36.9505 C 35.5244 0 34.3814 1.23317 34.3814 2.60364 V 4.62107 C 34.3814 4.62107 34.2952 5.93874 35.2953 7.73171 L 37.7484 12.2419 C 37.7484 12.2419 38.6326 13.755 38.6326 14.9036 V 17.1455 C 38.6326 17.7608 38.1474 18.2387 37.5489 18.2387 C 36.9505 18.2387 36.4652 17.7634 36.4652 17.1745 V 13.7576 C 35.953 13.982 35.1255 14.3464 34.9529 14.4019 C 34.6968 14.5419 34.3814 14.8508 34.3814 15.4951 V 17.5125 C 34.4111 18.8566 35.4948 19.9498 36.8642 20.0053 H 38.1474 C 39.5734 20.0053 40.7164 18.8857 40.7164 17.5125 V 15.0198 C 40.7164 15.0198 40.8027 13.7021 39.8026 11.9092 L 37.3494 7.36995 C 37.3494 7.36995 36.4652 5.88593 36.4652 4.73726 V 2.85978 C 36.4652 2.24452 36.9505 1.76657 37.5489 1.76657 C 38.1474 1.76657 38.6326 2.24188 38.6326 2.85978 V 5.12807 C 39.1745 4.90362 39.9724 4.53921 40.1449 4.45471 V 4.45207 Z M 20.1884 17.8162 V 11.0087 C 20.1884 10.7552 20.4175 10.5334 20.6736 10.5334 H 21.5309 C 22.1859 10.5334 22.4717 10.2535 22.6145 10 C 22.7008 9.831 23.0432 9.02033 23.2723 8.51598 H 20.1911 V 2.68814 C 20.1911 2.40824 20.3906 2.21283 20.6763 2.21283 H 22.3315 C 22.9865 2.21283 23.302 1.90388 23.4152 1.65302 L 24.0999 0.195405 H 18.765 C 18.4227 0.195405 18.1666 0.448904 18.1369 0.755215 V 19.8046 H 22.7008 C 23.3559 19.8046 23.6713 19.5247 23.8141 19.2712 C 23.9004 19.1022 24.2428 18.3179 24.4692 17.8136 H 20.1911 L 20.1884 17.8162 Z M 8.78541 0.195405 H 0.628107 C 0.285748 0.195405 0 0.47531 0 0.810668 V 2.18379 H 2.88174 V 19.3847 C 2.88174 19.5247 2.96801 19.8046 3.45324 19.8046 H 4.90894 V 2.66174 C 4.93859 2.40824 5.13808 2.18643 5.42113 2.18643 H 7.0197 C 7.67476 2.18643 7.99016 1.90652 8.10339 1.65302 L 8.7881 0.195405 H 8.78541 Z" fill="#FFF198"/></svg></div>
        <p style="font-size: 13px; color: rgba(255,255,255,0.7); margin: 0;">© 2025 Tessan. Tous droits réservés.</p>
      </footer>`,
    })

    // Catégorie: Layout
    editor.BlockManager.add('tessan-container', {
      label: `<div class="gjs-block-label">
        <div style="border: 2px dashed #ccc; padding: 8px; border-radius: 4px; font-size: 10px; margin-bottom: 4px; text-align: center;">[ ]</div>
        <span>Conteneur</span>
      </div>`,
      category: 'Layout',
      content: `<div style="max-width: 1200px; margin: 0 auto; padding: 24px;">
        <p style="color: #999; text-align: center;">Glissez des éléments ici</p>
      </div>`,
    })

    editor.BlockManager.add('tessan-flex-row', {
      label: `<div class="gjs-block-label">
        <div style="display: flex; gap: 2px; margin-bottom: 4px;">
          <div style="background: #e5e7eb; flex: 1; height: 20px; border-radius: 2px;"></div>
          <div style="background: #e5e7eb; flex: 1; height: 20px; border-radius: 2px;"></div>
        </div>
        <span>2 Colonnes</span>
      </div>`,
      category: 'Layout',
      content: `<div style="display: flex; gap: 24px; flex-wrap: wrap;">
        <div style="flex: 1; min-width: 200px; background: #f9fafb; padding: 16px; border-radius: 8px;">
          <p style="color: #666; margin: 0;">Colonne 1</p>
        </div>
        <div style="flex: 1; min-width: 200px; background: #f9fafb; padding: 16px; border-radius: 8px;">
          <p style="color: #666; margin: 0;">Colonne 2</p>
        </div>
      </div>`,
    })

    editor.BlockManager.add('tessan-flex-3col', {
      label: `<div class="gjs-block-label">
        <div style="display: flex; gap: 2px; margin-bottom: 4px;">
          <div style="background: #e5e7eb; flex: 1; height: 20px; border-radius: 2px;"></div>
          <div style="background: #e5e7eb; flex: 1; height: 20px; border-radius: 2px;"></div>
          <div style="background: #e5e7eb; flex: 1; height: 20px; border-radius: 2px;"></div>
        </div>
        <span>3 Colonnes</span>
      </div>`,
      category: 'Layout',
      content: `<div style="display: flex; gap: 24px; flex-wrap: wrap;">
        <div style="flex: 1; min-width: 150px; background: #f9fafb; padding: 16px; border-radius: 8px;">
          <p style="color: #666; margin: 0;">Colonne 1</p>
        </div>
        <div style="flex: 1; min-width: 150px; background: #f9fafb; padding: 16px; border-radius: 8px;">
          <p style="color: #666; margin: 0;">Colonne 2</p>
        </div>
        <div style="flex: 1; min-width: 150px; background: #f9fafb; padding: 16px; border-radius: 8px;">
          <p style="color: #666; margin: 0;">Colonne 3</p>
        </div>
      </div>`,
    })

    // Catégorie: Images & Médias
    editor.BlockManager.add('tessan-hero-image', {
      label: `<div class="gjs-block-label">
        <div style="background: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=100&h=60&fit=crop'); background-size: cover; height: 40px; border-radius: 4px; margin-bottom: 4px;"></div>
        <span>Hero Image</span>
      </div>`,
      category: 'Images',
      content: `<div style="background-image: url('https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&h=600&fit=crop'); background-size: cover; background-position: center; min-height: clamp(250px, 50vw, 400px); display: flex; align-items: center; justify-content: center; position: relative;">
        <div style="position: absolute; inset: 0; background: rgba(15, 53, 45, 0.7);"></div>
        <div style="position: relative; z-index: 1; text-align: center; padding: clamp(24px, 5vw, 48px) clamp(16px, 4vw, 24px); max-width: min(800px, 90vw); width: 100%;">
          <h1 style="font-family: 'Recoleta', serif; color: white; font-size: clamp(1.5rem, 5vw, 3rem); margin-bottom: 1rem;">Titre Principal</h1>
          <p style="color: rgba(255,255,255,0.9); font-size: clamp(1rem, 2.5vw, 1.25rem); margin-bottom: 2rem;">Sous-titre descriptif pour accompagner votre message principal</p>
          <a href="#" style="display: inline-block; background: #FFF198; color: #0F352D; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 600;">Découvrir</a>
        </div>
      </div>`,
    })

    editor.BlockManager.add('tessan-section-bg', {
      label: `<div class="gjs-block-label">
        <div style="background: linear-gradient(45deg, #0F352D 50%, transparent 50%), url('data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 20 20%22><rect fill=%22%23ddd%22 width=%2220%22 height=%2220%22/></svg>'); height: 40px; border-radius: 4px; margin-bottom: 4px;"></div>
        <span>Section + Fond</span>
      </div>`,
      category: 'Images',
      content: `<section style="background-image: url('https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=800&fit=crop'); background-size: cover; background-position: center; padding: clamp(40px, 10vw, 80px) clamp(16px, 4vw, 24px); position: relative;">
        <div style="position: absolute; inset: 0; background: linear-gradient(135deg, rgba(15, 53, 45, 0.9) 0%, rgba(29, 92, 77, 0.8) 100%);"></div>
        <div style="position: relative; z-index: 1; max-width: min(1200px, 90vw); margin: 0 auto;">
          <h2 style="font-family: 'Recoleta', serif; color: white; font-size: clamp(1.5rem, 4vw, 2.5rem); margin-bottom: 1rem; text-align: center;">Section avec image de fond</h2>
          <p style="color: rgba(255,255,255,0.85); font-size: clamp(1rem, 2vw, 1.125rem); text-align: center; max-width: min(600px, 90vw); margin: 0 auto;">Ajoutez du contenu ici. L'image de fond s'adapte à tous les écrans.</p>
        </div>
      </section>`,
    })

    editor.BlockManager.add('tessan-image-simple', {
      label: `<div class="gjs-block-label">
        <div style="font-size: 24px; margin-bottom: 4px;">🖼️</div>
        <span>Image</span>
      </div>`,
      category: 'Images',
      content: `<img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=500&fit=crop" alt="Image descriptive" style="width: 100%; height: auto; border-radius: 8px; display: block;" />`,
    })

    editor.BlockManager.add('tessan-image-caption', {
      label: `<div class="gjs-block-label">
        <div style="border: 1px solid #e5e7eb; border-radius: 4px; padding: 4px; margin-bottom: 4px;">
          <div style="background: #e5e7eb; height: 24px; border-radius: 2px;"></div>
          <div style="font-size: 8px; color: #999; margin-top: 2px;">légende</div>
        </div>
        <span>Image + Légende</span>
      </div>`,
      category: 'Images',
      content: `<figure style="margin: 0;">
        <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=500&fit=crop" alt="Image descriptive" style="width: 100%; height: auto; border-radius: 8px; display: block;" />
        <figcaption style="font-size: 14px; color: #6b7280; margin-top: 8px; text-align: center; font-style: italic;">Légende de l'image - cliquez pour modifier</figcaption>
      </figure>`,
    })

    // Catégorie: Carousel
    editor.BlockManager.add('tessan-carousel', {
      label: `<div class="gjs-block-label">
        <div style="display: flex; gap: 2px; margin-bottom: 4px;">
          <div style="background: #0F352D; flex: 2; height: 30px; border-radius: 2px;"></div>
          <div style="background: #ccc; flex: 1; height: 30px; border-radius: 2px;"></div>
        </div>
        <span>Carousel</span>
      </div>`,
      category: 'Images',
      content: `<div class="tessan-carousel" data-carousel data-autoplay="true" data-interval="10000" style="position: relative; overflow: hidden; border-radius: 12px;">
        <div class="carousel-track" data-carousel-track style="display: flex; transition: transform 0.5s ease-in-out;">
          <div class="carousel-slide" data-carousel-slide style="flex: 0 0 100%; min-width: 100%;">
            <div style="background-image: url('https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1200&h=500&fit=crop'); background-size: cover; background-position: center; min-height: clamp(250px, 50vw, 400px); display: flex; align-items: center; justify-content: center; padding: 16px;">
              <div style="background: rgba(15, 53, 45, 0.8); padding: clamp(20px, 4vw, 32px) clamp(24px, 5vw, 48px); border-radius: 12px; text-align: center; max-width: 90%;">
                <h3 style="font-family: 'Recoleta', serif; color: white; font-size: clamp(1.25rem, 4vw, 2rem); margin: 0 0 8px 0;">Slide 1</h3>
                <p style="color: rgba(255,255,255,0.9); margin: 0; font-size: clamp(0.875rem, 2vw, 1rem);">Description du premier slide</p>
              </div>
            </div>
          </div>
          <div class="carousel-slide" data-carousel-slide style="flex: 0 0 100%; min-width: 100%;">
            <div style="background-image: url('https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1200&h=500&fit=crop'); background-size: cover; background-position: center; min-height: clamp(250px, 50vw, 400px); display: flex; align-items: center; justify-content: center; padding: 16px;">
              <div style="background: rgba(15, 53, 45, 0.8); padding: clamp(20px, 4vw, 32px) clamp(24px, 5vw, 48px); border-radius: 12px; text-align: center; max-width: 90%;">
                <h3 style="font-family: 'Recoleta', serif; color: white; font-size: clamp(1.25rem, 4vw, 2rem); margin: 0 0 8px 0;">Slide 2</h3>
                <p style="color: rgba(255,255,255,0.9); margin: 0; font-size: clamp(0.875rem, 2vw, 1rem);">Description du deuxième slide</p>
              </div>
            </div>
          </div>
          <div class="carousel-slide" data-carousel-slide style="flex: 0 0 100%; min-width: 100%;">
            <div style="background-image: url('https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&h=500&fit=crop'); background-size: cover; background-position: center; min-height: clamp(250px, 50vw, 400px); display: flex; align-items: center; justify-content: center; padding: 16px;">
              <div style="background: rgba(15, 53, 45, 0.8); padding: clamp(20px, 4vw, 32px) clamp(24px, 5vw, 48px); border-radius: 12px; text-align: center; max-width: 90%;">
                <h3 style="font-family: 'Recoleta', serif; color: white; font-size: clamp(1.25rem, 4vw, 2rem); margin: 0 0 8px 0;">Slide 3</h3>
                <p style="color: rgba(255,255,255,0.9); margin: 0; font-size: clamp(0.875rem, 2vw, 1rem);">Description du troisième slide</p>
              </div>
            </div>
          </div>
        </div>
        <div class="carousel-dots" data-carousel-dots style="position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); display: flex; gap: 8px;">
          <button data-carousel-dot="0" style="width: 12px; height: 12px; border-radius: 50%; background: white; border: none; cursor: pointer; padding: 0;"></button>
          <button data-carousel-dot="1" style="width: 12px; height: 12px; border-radius: 50%; background: rgba(255,255,255,0.5); border: none; cursor: pointer; padding: 0;"></button>
          <button data-carousel-dot="2" style="width: 12px; height: 12px; border-radius: 50%; background: rgba(255,255,255,0.5); border: none; cursor: pointer; padding: 0;"></button>
        </div>
      </div>`,
    })

    editor.BlockManager.add('tessan-gallery', {
      label: `<div class="gjs-block-label">
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px; margin-bottom: 4px;">
          <div style="background: #e5e7eb; height: 16px; border-radius: 1px;"></div>
          <div style="background: #d1d5db; height: 16px; border-radius: 1px;"></div>
          <div style="background: #e5e7eb; height: 16px; border-radius: 1px;"></div>
        </div>
        <span>Galerie</span>
      </div>`,
      category: 'Images',
      content: `<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 16px;">
        <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=300&fit=crop" alt="Image 1" style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px;" />
        <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&h=300&fit=crop" alt="Image 2" style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px;" />
        <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop" alt="Image 3" style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px;" />
      </div>`,
    })

    editor.BlockManager.add('tessan-card-image', {
      label: `<div class="gjs-block-label">
        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 4px; overflow: hidden; margin-bottom: 4px;">
          <div style="background: #e5e7eb; height: 20px;"></div>
          <div style="padding: 4px; font-size: 8px;">Carte</div>
        </div>
        <span>Carte + Image</span>
      </div>`,
      category: 'Images',
      content: `<div style="background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.08);">
        <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600&h=300&fit=crop" alt="Image" style="width: 100%; height: 200px; object-fit: cover;" />
        <div style="padding: 20px;">
          <h3 style="font-family: 'Recoleta', serif; color: #0F352D; font-size: 1.25rem; margin: 0 0 8px 0;">Titre de la carte</h3>
          <p style="color: #6b7280; font-size: 14px; margin: 0 0 16px 0;">Description de la carte avec image. Cliquez pour modifier ce texte.</p>
          <a href="#" style="color: #0F352D; font-weight: 600; text-decoration: none;">En savoir plus →</a>
        </div>
      </div>`,
    })

    // Open blocks category by default
    editor.on('load', () => {
      const categories = editor.BlockManager.getCategories()
      categories.forEach((category: { set: (key: string, value: boolean) => void }) => {
        category.set('open', true)
      })

      // Inject carousel editor styles into the iframe
      const canvas = editor.Canvas.getDocument()
      if (canvas) {
        const carouselEditorStyles = canvas.createElement('style')
        carouselEditorStyles.id = 'carousel-editor-styles'
        carouselEditorStyles.textContent = `
          /* CAROUSEL EDITOR MODE - Show all slides stacked vertically */
          [data-carousel] {
            overflow: visible !important;
          }

          [data-carousel-track] {
            flex-direction: column !important;
            transform: none !important;
            transition: none !important;
          }

          [data-carousel-slide] {
            flex: none !important;
            min-width: 100% !important;
            margin-bottom: 8px;
            border: 2px dashed #0F352D;
            border-radius: 8px;
            position: relative;
          }

          [data-carousel-slide]::before {
            position: absolute;
            top: 8px;
            left: 8px;
            background: #0F352D;
            color: white;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 600;
            z-index: 100;
            pointer-events: none;
          }

          [data-carousel-slide]:nth-child(1)::before { content: 'Slide 1 - Cliquez pour modifier'; }
          [data-carousel-slide]:nth-child(2)::before { content: 'Slide 2 - Cliquez pour modifier'; }
          [data-carousel-slide]:nth-child(3)::before { content: 'Slide 3 - Cliquez pour modifier'; }
          [data-carousel-slide]:nth-child(4)::before { content: 'Slide 4 - Cliquez pour modifier'; }
          [data-carousel-slide]:nth-child(5)::before { content: 'Slide 5 - Cliquez pour modifier'; }

          /* Hide navigation dots in editor */
          [data-carousel-dots], .carousel-dots {
            display: none !important;
          }

          /* Visual indicator for carousel container */
          [data-carousel]::after {
            content: '🎠 Mode édition - Tous les slides sont visibles';
            display: block;
            background: #fcf5cc;
            color: #0F352D;
            padding: 8px 16px;
            text-align: center;
            font-size: 12px;
            font-weight: 500;
            border-radius: 0 0 12px 12px;
            margin-top: 8px;
          }
        `
        canvas.head.appendChild(carouselEditorStyles)
      }

      // Intercept paste events to warn about base64 images
      if (canvas) {
        canvas.addEventListener('paste', (e: ClipboardEvent) => {
          const items = e.clipboardData?.items
          if (items) {
            for (let i = 0; i < items.length; i++) {
              if (items[i].type.startsWith('image/')) {
                e.preventDefault()
                alert('⚠️ Le collage d\'images n\'est pas supporté.\n\nUtilisez l\'onglet "Images" pour ajouter une image via URL.')
                return
              }
            }
          }
        })

        // Intercept drop events to warn about base64 images
        canvas.addEventListener('drop', (e: DragEvent) => {
          const files = e.dataTransfer?.files
          if (files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
              if (files[i].type.startsWith('image/')) {
                e.preventDefault()
                alert('⚠️ Le glisser-déposer d\'images n\'est pas supporté.\n\nUtilisez l\'onglet "Images" pour ajouter une image via URL.')
                return
              }
            }
          }
        })
      }
    })

    // Auto-switch to traits tab when a link is selected
    editor.on('component:selected', (component) => {
      const tagName = component.get('tagName')?.toLowerCase()
      const isLink = tagName === 'a' || tagName === 'button'
      setHasLinkSelected(isLink)

      // Auto-switch to traits tab when link is selected
      if (isLink) {
        setActiveTab('traits')
      }
    })

    // Reset when component is deselected
    editor.on('component:deselected', () => {
      setHasLinkSelected(false)
      // Switch back to blocks tab if we were on traits
      setActiveTab((current) => current === 'traits' ? 'blocks' : current)
    })

    // Load initial content
    if (initialGjsData && Object.keys(initialGjsData).length > 0) {
      editor.loadProjectData(initialGjsData)
    } else if (initialHtml || initialCss) {
      editor.setComponents(initialHtml)
      editor.setStyle(initialCss)
    }

    editorInstance.current = editor
    setIsReady(true)

    return () => {
      editor.destroy()
      editorInstance.current = null
    }
  }, [initialHtml, initialCss, initialGjsData])

  return (
    <div className={`flex flex-col bg-gray-100 ${isFullscreen ? 'fixed inset-0 z-50 h-screen' : 'h-full'}`}>
      {/* Top Toolbar */}
      <div className="flex items-center justify-between p-2 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-1">
          {/* Undo/Redo */}
          <button
            onClick={handleUndo}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Annuler (Ctrl+Z)"
            disabled={!isReady}
          >
            <Undo className="w-4 h-4" />
          </button>
          <button
            onClick={handleRedo}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refaire (Ctrl+Y)"
            disabled={!isReady}
          >
            <Redo className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2" />

          {/* Device switcher */}
          <button
            onClick={() => handleDeviceChange('Desktop')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Bureau"
            disabled={!isReady}
          >
            <Monitor className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeviceChange('Tablet')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Tablette"
            disabled={!isReady}
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeviceChange('Mobile')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Mobile"
            disabled={!isReady}
          >
            <Smartphone className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2" />

          {/* Tools */}
          <button
            onClick={handleToggleCode}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Voir le code HTML"
            disabled={!isReady}
          >
            <Code className="w-4 h-4" />
          </button>
          <button
            onClick={handleClear}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Tout effacer"
            disabled={!isReady}
          >
            <Trash2 className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-gray-200 mx-2" />

          {/* Fullscreen toggle */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={isFullscreen ? "Quitter le plein écran" : "Plein écran"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>

        <div className="flex items-center gap-2">
          {onPreview && (
            <Button variant="outline" size="sm" onClick={onPreview} disabled={!isReady}>
              <Eye className="w-4 h-4 mr-1" />
              Aperçu
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving || !isReady}>
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas */}
        <div className="flex-1 relative overflow-hidden bg-gray-200">
          <div ref={editorRef} className="absolute inset-0 gjs-editor-container" />
        </div>

        {/* Sidebar Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-l-lg p-1 shadow-sm hover:bg-gray-50"
          style={{ right: sidebarOpen ? '384px' : '0' }}
        >
          {sidebarOpen ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        {/* Right Sidebar */}
        {sidebarOpen && (
          <div className="w-96 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('blocks')}
                className={`flex-1 flex items-center justify-center gap-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'blocks'
                    ? 'text-tessan-primary border-b-2 border-tessan-primary bg-tessan-cream/30'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
                Blocs
              </button>
              <button
                onClick={() => setActiveTab('layers')}
                className={`flex-1 flex items-center justify-center gap-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'layers'
                    ? 'text-tessan-primary border-b-2 border-tessan-primary bg-tessan-cream/30'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Layers className="w-4 h-4" />
                Layers
              </button>
              <button
                onClick={() => setActiveTab('styles')}
                className={`flex-1 flex items-center justify-center gap-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'styles'
                    ? 'text-tessan-primary border-b-2 border-tessan-primary bg-tessan-cream/30'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Palette className="w-4 h-4" />
                Styles
              </button>
              {/* Only show Lien tab when a link/button is selected */}
              {hasLinkSelected && (
                <button
                  onClick={() => setActiveTab('traits')}
                  className={`flex-1 flex items-center justify-center gap-1 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === 'traits'
                      ? 'text-tessan-primary border-b-2 border-tessan-primary bg-tessan-cream/30'
                      : 'text-tessan-primary hover:bg-tessan-cream/20 animate-pulse'
                  }`}
                >
                  <Settings2 className="w-4 h-4" />
                  Lien
                  {activeTab !== 'traits' && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-tessan-primary rounded-full" />
                  )}
                </button>
              )}
              <button
                onClick={() => setActiveTab('assets')}
                className={`flex-1 flex items-center justify-center gap-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === 'assets'
                    ? 'text-tessan-primary border-b-2 border-tessan-primary bg-tessan-cream/30'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ImageIcon className="w-4 h-4" />
                Images
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-auto">
              <div
                id="blocks-container"
                className={`h-full ${activeTab === 'blocks' ? 'block' : 'hidden'}`}
              />
              <div
                id="layers-container"
                className={`h-full ${activeTab === 'layers' ? 'block' : 'hidden'}`}
              />
              <div
                id="styles-container"
                className={`h-full ${activeTab === 'styles' ? 'block' : 'hidden'}`}
              />
              {/* Traits Panel (for links, attributes) */}
              <div className={`h-full ${activeTab === 'traits' ? 'block' : 'hidden'}`}>
                <div id="traits-container" className="p-4" />
                <div className="px-4 pb-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                    <strong>Comment modifier un lien :</strong>
                    <ol className="mt-1 list-decimal list-inside space-y-1">
                      <li>Sélectionnez un bouton ou lien dans le canvas</li>
                      <li>Modifiez l'URL dans le champ "href" ci-dessus</li>
                      <li>Choisissez "target" pour ouvrir dans un nouvel onglet</li>
                    </ol>
                  </div>
                </div>
              </div>
              {/* Assets Panel */}
              <div className={`h-full p-4 overflow-y-auto ${activeTab === 'assets' ? 'block' : 'hidden'}`}>
                {/* Upload Zone */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                    Uploader une image
                  </label>
                  <div
                    onDrop={handleDropFile}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`
                      border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                      ${isDraggingFile
                        ? 'border-tessan-primary bg-tessan-primary/5'
                        : 'border-gray-300 hover:border-tessan-primary hover:bg-gray-50'
                      }
                      ${isUploading ? 'pointer-events-none opacity-60' : ''}
                    `}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    {isUploading ? (
                      <div className="space-y-2">
                        <Loader2 className="w-6 h-6 mx-auto text-tessan-primary animate-spin" />
                        <p className="text-sm text-gray-600">Upload... {progress}%</p>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-tessan-primary h-1.5 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-6 h-6 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          Glissez une image ou <span className="text-tessan-primary font-medium">cliquez</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          JPEG, PNG, GIF, WebP (max 10MB)
                        </p>
                      </>
                    )}
                  </div>
                  {uploadError && (
                    <p className="text-xs text-red-600 mt-2">{uploadError}</p>
                  )}
                </div>

                {/* URL Input */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                    Ou entrez une URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={customImageUrl}
                      onChange={(e) => setCustomImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tessan-primary/20 focus:border-tessan-primary"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddCustomUrl()}
                    />
                    <button
                      onClick={handleAddCustomUrl}
                      disabled={!customImageUrl.trim()}
                      className="px-3 py-2 bg-tessan-primary text-white rounded-lg text-sm font-medium hover:bg-tessan-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Ajouter
                    </button>
                  </div>
                </div>

                {/* Stock Images */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
                    Bibliothèque d'images
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {STOCK_IMAGES.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => handleInsertImage(img.src)}
                        className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-gray-200 hover:border-tessan-primary hover:shadow-md transition-all"
                      >
                        <img
                          src={img.src}
                          alt={img.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            {img.name}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Instructions */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                  <strong>Comment ajouter une image :</strong>
                  <ol className="mt-1 list-decimal list-inside space-y-1">
                    <li>Uploadez depuis votre ordinateur (drag & drop ou clic)</li>
                    <li>Ou collez une URL d'image externe</li>
                    <li>Ou cliquez sur une image de la bibliothèque</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Help hint */}
            <div className="p-3 bg-tessan-cream/50 border-t border-gray-200 text-xs text-gray-600">
              <strong>Astuce :</strong> Glissez les blocs vers la zone d'édition. Double-cliquez sur un texte pour le modifier.
            </div>
          </div>
        )}
      </div>

      {/* Custom GrapesJS styles */}
      <style>{`
        /* Hide default GrapesJS panels */
        .gjs-editor-container .gjs-pn-panels {
          display: none !important;
        }

        /* Canvas styling */
        .gjs-editor-container .gjs-cv-canvas {
          width: 100% !important;
          height: 100% !important;
          top: 0 !important;
          left: 0 !important;
        }

        .gjs-editor-container .gjs-frame-wrapper {
          padding: 20px;
          background: #e5e7eb;
        }

        .gjs-editor-container .gjs-frame {
          background: white;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }

        /* ===================== */
        /* CAROUSEL EDITOR MODE  */
        /* Show all slides stacked vertically for easy editing */
        /* ===================== */
        .gjs-frame [data-carousel] {
          overflow: visible !important;
        }

        .gjs-frame [data-carousel-track] {
          flex-direction: column !important;
          transform: none !important;
          transition: none !important;
        }

        .gjs-frame [data-carousel-slide] {
          flex: none !important;
          min-width: 100% !important;
          margin-bottom: 8px;
          border: 2px dashed #0F352D;
          border-radius: 8px;
          position: relative;
        }

        .gjs-frame [data-carousel-slide]::before {
          content: 'Slide ' attr(data-slide-index);
          position: absolute;
          top: 8px;
          left: 8px;
          background: #0F352D;
          color: white;
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          z-index: 100;
          pointer-events: none;
        }

        .gjs-frame [data-carousel-slide]:nth-child(1)::before {
          content: 'Slide 1 - Cliquez pour modifier';
        }

        .gjs-frame [data-carousel-slide]:nth-child(2)::before {
          content: 'Slide 2 - Cliquez pour modifier';
        }

        .gjs-frame [data-carousel-slide]:nth-child(3)::before {
          content: 'Slide 3 - Cliquez pour modifier';
        }

        .gjs-frame [data-carousel-slide]:nth-child(4)::before {
          content: 'Slide 4 - Cliquez pour modifier';
        }

        .gjs-frame [data-carousel-slide]:nth-child(5)::before {
          content: 'Slide 5 - Cliquez pour modifier';
        }

        /* Hide navigation dots in editor mode - not needed when all slides visible */
        .gjs-frame [data-carousel-dots],
        .gjs-frame .carousel-dots {
          display: none !important;
        }

        /* Visual indicator for carousel container */
        .gjs-frame [data-carousel]::after {
          content: '🎠 Mode édition carrousel - Tous les slides sont visibles';
          display: block;
          background: #fcf5cc;
          color: #0F352D;
          padding: 8px 16px;
          text-align: center;
          font-size: 12px;
          font-weight: 500;
          border-radius: 0 0 12px 12px;
          margin-top: 8px;
        }

        /* Block manager styling */
        #blocks-container .gjs-blocks-c {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          padding: 12px;
        }

        #blocks-container .gjs-block-category {
          background: transparent;
        }

        #blocks-container .gjs-block-category .gjs-title {
          background: #f3f4f6;
          border: none;
          padding: 8px 12px;
          font-weight: 600;
          color: #374151;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        #blocks-container .gjs-block {
          min-height: 70px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: white;
          padding: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: grab;
          transition: all 0.2s;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }

        #blocks-container .gjs-block:hover {
          border-color: #0F352D;
          background: #fcf5cc;
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        #blocks-container .gjs-block.gjs-block__drag {
          opacity: 0.5;
        }

        #blocks-container .gjs-block-label {
          text-align: center;
          font-size: 11px;
          color: #4b5563;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* Layer manager styling */
        #layers-container {
          padding: 8px;
        }

        #layers-container .gjs-layer {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          margin-bottom: 4px;
        }

        #layers-container .gjs-layer-name {
          padding: 8px;
          font-size: 12px;
        }

        #layers-container .gjs-layer.gjs-selected {
          background: #fcf5cc;
          border-color: #0F352D;
        }

        /* Style manager styling */
        #styles-container {
          padding: 12px;
        }

        #styles-container .gjs-sm-sector {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 8px;
          overflow: hidden;
        }

        #styles-container .gjs-sm-sector-title {
          background: #f9fafb;
          padding: 10px 12px;
          font-weight: 600;
          font-size: 12px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        #styles-container .gjs-sm-properties {
          padding: 12px;
        }

        #styles-container .gjs-sm-property {
          margin-bottom: 12px;
        }

        #styles-container .gjs-sm-label {
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        #styles-container .gjs-field {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          overflow: hidden;
        }

        #styles-container .gjs-field input {
          padding: 8px;
          font-size: 13px;
          border: none;
          width: 100%;
        }

        #styles-container .gjs-field input:focus {
          outline: none;
          box-shadow: 0 0 0 2px #fcf5cc;
        }

        /* Selected component highlight */
        .gjs-selected {
          outline: 2px solid #0F352D !important;
          outline-offset: 2px;
        }

        /* Toolbar styling */
        .gjs-toolbar {
          background: #0F352D !important;
          border-radius: 4px;
        }

        .gjs-toolbar-item {
          color: white !important;
        }

        /* Rich text editor */
        .gjs-rte-toolbar {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        /* Selector manager */
        #styles-container .gjs-clm-tags {
          padding: 8px 12px;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        #styles-container .gjs-clm-tag {
          background: #0F352D;
          color: white;
          border-radius: 4px;
          padding: 4px 8px;
          font-size: 11px;
        }

        /* Trait manager styling */
        #traits-container .gjs-trt-traits {
          padding: 0;
        }

        #traits-container .gjs-trt-trait {
          display: flex;
          flex-direction: column;
          padding: 16px 0;
          border-bottom: 1px solid #e5e7eb;
        }

        #traits-container .gjs-trt-trait:last-child {
          border-bottom: none;
        }

        #traits-container .gjs-trt-trait__wrp {
          display: flex;
          flex-direction: column;
          gap: 8px;
          width: 100%;
        }

        #traits-container .gjs-label-wrp {
          width: 100%;
          margin-bottom: 0;
        }

        #traits-container .gjs-label {
          font-size: 12px;
          font-weight: 700;
          color: #0F352D;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          background: white;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-block;
        }

        #traits-container .gjs-field-wrp {
          width: 100%;
        }

        #traits-container .gjs-field {
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
          width: 100%;
          transition: border-color 0.2s;
        }

        #traits-container .gjs-field:focus-within {
          border-color: #0F352D;
        }

        #traits-container .gjs-field input,
        #traits-container .gjs-field select {
          padding: 12px 14px;
          font-size: 14px;
          border: none;
          width: 100%;
          background: white;
          font-family: inherit;
        }

        #traits-container .gjs-field input {
          font-family: monospace;
          font-size: 13px;
        }

        #traits-container .gjs-field input:focus,
        #traits-container .gjs-field select:focus {
          outline: none;
        }

        #traits-container .gjs-field select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 36px;
        }

        /* Trait type specific styling */
        #traits-container .gjs-trt-trait[data-trait="href"] .gjs-field input {
          color: #0F352D;
        }

        /* Empty state for traits */
        #traits-container:empty::after {
          content: 'Sélectionnez un élément pour voir ses attributs';
          display: block;
          padding: 24px 16px;
          text-align: center;
          color: #9ca3af;
          font-size: 13px;
          background: #f9fafb;
          border-radius: 8px;
          margin: 8px;
        }
      `}</style>
    </div>
  )
}
